import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Text,
  Card,
  FilterChips,
  StatsGrid,
  SearchInput,
  QueryChips,
} from '@teals/ui-web';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface BudgetPartner {
  name: string;
  used: number;
  cap: number;
}

const DATE_FILTERS = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: 'custom', label: 'Custom' },
];

const STATS = [
  { label: 'Total Exchanges', value: '12,847' },
  { label: 'Active Agents', value: '6' },
  { label: 'Active Partners', value: '3' },
  { label: 'Total Spend', value: '$21,600' },
];

const CHART_DATA = [
  { month: 'Jan', exchanges: 1820, spend: 2100 },
  { month: 'Feb', exchanges: 2140, spend: 2450 },
  { month: 'Mar', exchanges: 1650, spend: 1900 },
  { month: 'Apr', exchanges: 2380, spend: 2700 },
  { month: 'May', exchanges: 2950, spend: 3200 },
  { month: 'Jun', exchanges: 3420, spend: 3800 },
  { month: 'Jul', exchanges: 2800, spend: 3100 },
  { month: 'Aug', exchanges: 3100, spend: 3500 },
  { month: 'Sep', exchanges: 2600, spend: 2900 },
  { month: 'Oct', exchanges: 3750, spend: 4100 },
  { month: 'Nov', exchanges: 4200, spend: 4600 },
  { month: 'Dec', exchanges: 3800, spend: 4200 },
];

const BUDGET_PARTNERS: BudgetPartner[] = [
  { name: 'Acme Corp', used: 7200, cap: 10000 },
  { name: 'TechFlow Inc', used: 3400, cap: 8000 },
  { name: 'GlobalRetail', used: 9800, cap: 10000 },
  { name: 'FinanceHub Ltd', used: 1200, cap: 5000 },
];

const SUGGESTED_QUERIES = [
  { label: 'Top performing agent this month' },
  { label: 'Budget utilization by partner' },
  { label: 'Usage trend last 90 days' },
  { label: 'Which agents have the lowest latency?' },
  { label: 'Partner spend breakdown' },
];

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

const ChartCard = styled(Card)`
  border: 1px solid var(--border);
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const BudgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const BudgetCard = styled(Card)`
  border: 1px solid var(--border);
`;

const BudgetName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: var(--bg-inset);
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ $pct: number; $warn: boolean }>`
  height: 100%;
  border-radius: 4px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $warn }) => ($warn ? 'var(--warning)' : 'var(--accent)')};
  transition: width 0.3s ease;
`;

const BudgetMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const AskSection = styled(Card)`
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const AskTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Analytics: React.FC = () => {
  const [dateRange, setDateRange] = useState('30d');
  const [nlQuery, setNlQuery] = useState('');

  const handleQuerySelect = (label: string) => {
    setNlQuery(label);
  };

  return (
    <PageWrapper>
      <Header>
        <Text variant="h2">Analytics Center</Text>
        <FilterChips
          items={DATE_FILTERS}
          activeId={dateRange}
          onChange={setDateRange}
        />
      </Header>

      <Section>
        <StatsGrid items={STATS} columns={4} />
      </Section>

      <Section>
        <SectionTitle>Consumption Over Time</SectionTitle>
        <ChartCard padding={24} elevation="sm">
          <ChartHeader>
            <Text variant="h4">Monthly Activity</Text>
          </ChartHeader>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={CHART_DATA}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow-md)',
                  fontSize: '0.8125rem',
                }}
                labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
              />
              <Bar
                dataKey="exchanges"
                name="Exchanges"
                fill="var(--accent)"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="spend"
                name="Spend ($)"
                fill="var(--accent-muted)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      <Section>
        <SectionTitle>Budget Utilization by Partner</SectionTitle>
        <BudgetGrid>
          {BUDGET_PARTNERS.map((bp) => {
            const pct = (bp.used / bp.cap) * 100;
            return (
              <BudgetCard key={bp.name} padding={20} elevation="sm">
                <BudgetName>{bp.name}</BudgetName>
                <ProgressBarOuter>
                  <ProgressBarInner $pct={pct} $warn={pct >= 90} />
                </ProgressBarOuter>
                <BudgetMeta>
                  <span>${bp.used.toLocaleString()} used</span>
                  <span>{Math.round(pct)}%</span>
                  <span>${bp.cap.toLocaleString()} cap</span>
                </BudgetMeta>
              </BudgetCard>
            );
          })}
        </BudgetGrid>
      </Section>

      <Section>
        <SectionTitle>Ask About Your Data</SectionTitle>
        <AskSection padding={24} elevation="sm">
          <AskTitle>
            <span>💬</span>
            Ask a question about your analytics
          </AskTitle>
          <SearchInput
            value={nlQuery}
            onChange={setNlQuery}
            placeholder="e.g. Which agent handled the most exchanges last month?"
            style={{ maxWidth: '100%' }}
          />
          <QueryChips chips={SUGGESTED_QUERIES} onSelect={handleQuerySelect} />
        </AskSection>
      </Section>
    </PageWrapper>
  );
};

export default Analytics;
